import { Formik } from 'formik'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { Button, Checkbox, Form, Input, Textarea } from 'components/Forms'
import BaseModal from 'components/Modals/BaseModal'
import { ScrollableUserList, CostIcon } from 'components/UI'
import { Trans } from 'react-i18next'
import Api from 'services/Api'
import { TrackAction } from 'services/TrackAction'
import { closeModal } from 'store/actions/modals'
import { createErrorNotification, createNotification } from 'store/actions/notifications'
import { Card } from 'ui'
import { t } from 'translations'

const UserOnboardingModal = ({ userIds }) => {
  const dispatch = useDispatch()
  const {
    decodedToken: { uid: currentUserId }
  } = useSelector(state => state.auth)
  const { usersById } = useSelector(state => state.resources)
  const { companyNameFr = 'Ton employeur', companyNameEn = 'Your employer', disableTwilio } = useSelector(state => state.settings)

  const onboard = async ({ onboardingMessage, removeDefaultMessage, sendSmsOnboarding, reinviteUsers }, meOnly) => {
    // Filter out the users that have already been invited AND we do not wish to invite them again
    const filteredUserIds = userIds.filter(userId => !usersById[userId]?.onboarding || reinviteUsers)

    const { success } = await Api.post(`users/onboard`, {
      onboardingMessage,
      removeDefaultMessage,
      sendSmsOnboarding,
      reinviteUsers,
      userIds: meOnly ? [currentUserId] : filteredUserIds
    })
    if (!success) return dispatch(createErrorNotification())

    // Track that user has created a user
    TrackAction('ONBOARD_USER')

    dispatch(
      createNotification({
        title: t('manager.users.onboardingModal.successNotif.TITLE'),
        description: t('manager.users.onboardingModal.successNotif.DESCRIPTION')
      })
    )
    dispatch(closeModal())
  }

  return (
    <BaseModal
      hideButtons
      title="manager.users.onboardingModal"
      titleVar={{ user: userIds.length > 1 ? t('manager.users.onboardingModal.TITLE_VAR') : usersById[userIds[0]]?.fullName }}
    >
      <Formik
        enableReinitialize={true}
        initialValues={{ onboardingMessage: '', removeDefaultMessage: false, sendSmsOnboarding: !disableTwilio, reinviteUsers: false }}
        onSubmit={values => onboard(values)}
      >
        {({ values, isSubmitting }) => {
          const numberInviting = userIds.filter(userId => !usersById[userId]?.onboarding || values.reinviteUsers).length

          return (
            <Form className="mt-2">
              {userIds.length > 1 ? <ScrollableUserList usersIds={userIds} /> : null}
              <h6 className="mt-4">{t('manager.users.onboardingModal.LABEL')}</h6>
              <Card className="bg-light pt-3">
                <div className="px-3">
                  <pre>
                    <Trans t={t} i18nKey={`manager.users.onboardingModal.${userIds.length > 1 ? 'EXAMPLE' : 'EXAMPLE_SINGLE_USER'}`}>
                      {{
                        message: !values.removeDefaultMessage
                          ? t('manager.users.onboardingModal.EXAMPLE_DEFAUT_MESSAGE', { companyNameFr, companyNameEn })
                          : null
                      }}
                      <b>
                        {{ onboardingMessage: !values.onboardingMessage ? t('manager.users.onboardingModal.YOUR_MESSAGE_HERE') : values.onboardingMessage }}
                      </b>
                      {{ user: usersById[userIds[0]]?.firstName }}
                      {{ employeeNumber: usersById[userIds[0]]?.employeeNumber }}
                      {{ username: usersById[userIds[0]]?.username }}
                    </Trans>
                  </pre>
                </div>
              </Card>
              <Textarea module="manager.users.onboardingModal" name="onboardingMessage" className="mt-2" />

              <Checkbox module="manager.users.onboardingModal" name="removeDefaultMessage" />
              {!disableTwilio ? (
                <div className="d-flex flex-row">
                  <Checkbox module="manager.users.onboardingModal" name="sendSmsOnboarding" />
                  <div className="ml-1 text-align-center">
                    <CostIcon />
                  </div>
                </div>
              ) : null}
              <Checkbox module="manager.users.onboardingModal" name="reinviteUsers" />

              {values.reinviteUsers ? <Input module="manager.users.onboardingModal" name="confirmNumberReinvite" type="number" step="1" /> : null}

              <div className="mt-2">
                <Button
                  className="btn btn-primary mr-2"
                  type="submit"
                  isSubmitting={isSubmitting}
                  disabled={values.reinviteUsers ? values.confirmNumberReinvite !== numberInviting : null}
                >
                  {t(`manager.users.onboardingModal.INVITE_SUBMIT`, {
                    count: numberInviting
                  })}
                </Button>
                <Button
                  type="button"
                  text="manager.users.onboardingModal.SEND_TEST"
                  className="btn btn-outline-secondary mr-2"
                  onClick={() => onboard(values, true)}
                  disabled={currentUserId === '000000000000000000000000'}
                />
                <Button className="btn btn-outline-secondary" onClick={() => dispatch(closeModal())}>
                  {t(`actions.CLOSE`)}
                </Button>
              </div>
            </Form>
          )
        }}
      </Formik>
    </BaseModal>
  )
}

export default UserOnboardingModal
